
import { Toaster } from "@/components/ui/toaster";
import { SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";
import { pageView, initializeAnalytics, GA_MEASUREMENT_ID } from "./utils/analytics";
import CookieConsent from "./components/cookie/CookieConsent";
import ProtectedRoute from "./components/auth/ProtectedRoute";

// Lazy load the Analytics component
const Analytics = lazy(() => import("./components/ui/analytics").then(module => ({ 
  default: module.Analytics 
})));

// Lazy load pages to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Auth = lazy(() => import("./pages/Auth"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EmailConfirmed = lazy(() => import("./pages/EmailConfirmed"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Contact = lazy(() => import("./pages/Contact"));
const Book = lazy(() => import("./pages/Book"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin")); 
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LocationPage = lazy(() => import("./pages/LocationPage"));

// Lazy load service pages
const ManagedITServices = lazy(() => import("./pages/Services/ManagedITServices"));
const Cybersecurity = lazy(() => import("./pages/Services/Cybersecurity"));
const NetworkInfrastructure = lazy(() => import("./pages/Services/NetworkInfrastructure")); 
const CloudSolutions = lazy(() => import("./pages/Services/CloudSolutions"));
const ProjectConsulting = lazy(() => import("./pages/Services/ProjectConsulting")); 
const SpecialtyServices = lazy(() => import("./pages/Services/SpecialtyServices"));
const ResidentialServices = lazy(() => import("./pages/Services/ResidentialServices"));

// Lazy load blog pages
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogGeneratorPage = lazy(() => import("./pages/BlogGeneratorPage"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-itmgmt-bright-blue"></div>
  </div>
);

// Analytics tracker component - optimized
const AnalyticsTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Only initialize analytics when the component mounts
    // and do it with a slight delay to prioritize UI rendering
    const timeoutId = setTimeout(() => {
      initializeAnalytics();
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    // Only track page views on actual navigation, not initial load
    if (navigationType !== 'POP') {
      // Add a small delay to ensure the page has rendered
      const timeoutId = setTimeout(() => {
        pageView(location.pathname + location.search);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location, navigationType]);

  return null;
};

// Helper to determine if we're on the employee portal subdomain
const isPortalSubdomain = () => {
  const hostname = window.location.hostname;
  const isPortal = hostname === 'portal.itmgmtsol.com' ||
    (hostname.includes('localhost') && window.location.pathname.startsWith('/portal'));
  
  console.log('Hostname check:', hostname, 'Is Portal:', isPortal);
  return isPortal;
};

function App() {
  // Only show portal UI if we're specifically on the portal subdomain
  if (isPortalSubdomain()) {
    return (
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <TooltipProvider>
            <Analytics measurementId={GA_MEASUREMENT_ID} />
            <Toaster />
            <SonnerToaster position="top-right" />
            <BrowserRouter>
              <AnalyticsTracker />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRole="admin">
                      <Admin />
                    </ProtectedRoute>
                  } />
                  {/* Add Blog Generator to the admin portal */}
                  <Route path="/blog/generator" element={
                    <ProtectedRoute requiredRole="admin">
                      <BlogGeneratorPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/portal" element={<EmployeePortal />} />
                  <Route path="/auth" element={<EmployeePortal />} /> {/* Redirect /auth to EmployeePortal on subdomain */}
                  <Route path="/" element={<EmployeePortal />} /> {/* Redirect root to EmployeePortal on subdomain */}
                  <Route path="*" element={<EmployeePortal />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </HelmetProvider>
      </QueryClientProvider>
    );
  }

  // Full application for all other cases
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          {/* Lazy load Analytics component */}
          <Suspense fallback={null}>
            <Analytics measurementId={GA_MEASUREMENT_ID} />
          </Suspense>
          <Toaster />
          <SonnerToaster position="top-right" />
          <BrowserRouter>
            <AnalyticsTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} /> 
                <Route path="/auth" element={<Auth />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/portal" element={<EmployeePortal />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/email-confirmed" element={<EmailConfirmed />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/book" element={<Book />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/admin" element={
                  <ProtectedRoute requiredRole="admin">
                    <Admin />
                  </ProtectedRoute>
                } />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                
                {/* Service Pages */}
                <Route path="/services/managed-it" element={<ManagedITServices />} />
                <Route path="/services/cybersecurity" element={<Cybersecurity />} />
                <Route path="/services/network-infrastructure" element={<NetworkInfrastructure />} />
                <Route path="/services/cloud-solutions" element={<CloudSolutions />} />
                <Route path="/services/project-consulting" element={<ProjectConsulting />} />
                <Route path="/services/specialty-services" element={<SpecialtyServices />} />
                <Route path="/services/residential" element={<ResidentialServices />} />
                
                {/* Blog Pages */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                
                {/* Dynamic Location Pages - this route handles county-town patterns */}
                <Route path="/:slug" element={<LocationPage />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </BrowserRouter>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
